'use client';

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';

import { useNativeTokens } from '~/(tenant)/_components/native/_lib/use-native-tokens';

import type {
  StatKey,
  StatsStripSectionConfig,
} from '~/(tenant)/_lib/schema/native-home-config.schema';

interface StatsStripSectionProps {
  config: StatsStripSectionConfig;
  accountId: string;
}

const STAT_LABEL_KEYS: Record<StatKey, string> = {
  classes_attended: 'common:nativeHome.stats.classesAttended',
  credits_remaining: 'common:nativeHome.stats.creditsRemaining',
  streak_days: 'common:nativeHome.stats.streakDays',
  bookings_made: 'common:nativeHome.stats.bookingsMade',
  hours_played: 'common:nativeHome.stats.hoursPlayed',
  membership_tier: 'common:nativeHome.stats.membershipTier',
};

/**
 * 2-4 stats in a hairline-bordered row. Display serif numbers, mono labels.
 * No card chrome — just borderTop/Bottom + vertical dividers between cells.
 *
 * Hides entirely when not authenticated.
 */
export function StatsStripSection({ config, accountId }: StatsStripSectionProps) {
  const t = useNativeTokens();
  const { t: translate } = useTranslation();
  const client = useSupabase();

  const { data: session } = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const { data } = await client.auth.getSession();
      return data.session;
    },
    staleTime: 60_000,
  });

  const userId = session?.user?.id;
  const wanted = config.stats;

  const { data: stats } = useQuery({
    queryKey: ['stats-strip', userId, accountId, wanted.join(',')],
    queryFn: async () => {
      if (!userId) return null;
      return await fetchStats(client, userId, accountId, wanted);
    },
    enabled: !!userId,
    staleTime: 60_000,
  });

  if (!userId || !stats) return null;

  return (
    <section
      style={{
        margin: '24px 22px',
        padding: '20px 4px',
        borderTop: `1px solid ${t.line}`,
        borderBottom: `1px solid ${t.line}`,
        display: 'grid',
        gridTemplateColumns: `repeat(${wanted.length}, 1fr)`,
      }}
    >
      {wanted.map((key, i) => {
        const value = stats[key];
        return (
          <div
            key={key}
            style={{
              textAlign: 'center',
              borderLeft: i > 0 ? `1px solid ${t.line}` : 'none',
            }}
          >
            <div
              style={{
                fontFamily: t.fontDisplay,
                fontSize: 28,
                fontWeight: 300,
                color: t.foreground,
                lineHeight: 1,
              }}
            >
              {value ?? '—'}
            </div>
            <div
              style={{
                fontFamily: t.fontMono,
                fontSize: 8,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: t.muted,
                marginTop: 6,
              }}
            >
              {translate(STAT_LABEL_KEYS[key])}
            </div>
          </div>
        );
      })}
    </section>
  );
}

async function fetchStats(
  rawClient: unknown,
  userId: string,
  accountId: string,
  keys: readonly StatKey[],
): Promise<Record<StatKey, string | number | null>> {
  // Cast: several queries reference tables/RPCs whose generated types are
  // narrow in this codepath. Stats are best-effort — return null on error.
  const client = rawClient as any;
  const wanted = new Set(keys);
  const result: Record<StatKey, string | number | null> = {
    classes_attended: null,
    credits_remaining: null,
    streak_days: null,
    bookings_made: null,
    hours_played: null,
    membership_tier: null,
  };

  const promises: Promise<void>[] = [];

  if (wanted.has('credits_remaining')) {
    promises.push(
      (async () => {
        const nowIso = new Date().toISOString();
        const { data } = await client
          .from('user_booking_packages')
          .select('remaining_booking_count')
          .eq('user_id', userId)
          .eq('account_id', accountId)
          .eq('status', 'active')
          .gt('expires_at', nowIso);
        const rows = (data ?? []) as Array<{
          remaining_booking_count?: number | null;
        }>;
        const total = rows.reduce(
          (sum, r) => sum + (r.remaining_booking_count ?? 0),
          0,
        );
        result.credits_remaining = total;
      })(),
    );
  }

  if (wanted.has('classes_attended') || wanted.has('bookings_made')) {
    promises.push(
      (async () => {
        const { data } = await (client.rpc as any)('get_family_bookings', {
          p_user_id: userId,
          p_tenant_id: accountId,
          p_include_past: true,
        });
        const all = ((data ?? []) as any[]).flatMap((account: any) => account.bookings ?? []);
        if (wanted.has('classes_attended')) {
          result.classes_attended = all.filter(
            (b: any) => b.is_class_enrollment && b.booking_status === 'confirmed',
          ).length;
        }
        if (wanted.has('bookings_made')) {
          result.bookings_made = all.length;
        }
      })(),
    );
  }

  if (wanted.has('hours_played')) {
    promises.push(
      (async () => {
        const { data } = await (client.rpc as any)('get_family_bookings', {
          p_user_id: userId,
          p_tenant_id: accountId,
          p_include_past: true,
        });
        const all = ((data ?? []) as any[]).flatMap((account: any) => account.bookings ?? []);
        const minutes = all.reduce((sum: number, b: any) => {
          const [sh, sm] = (b.start_time ?? '00:00').split(':').map(Number);
          const [eh, em] = (b.end_time ?? '00:00').split(':').map(Number);
          return sum + ((eh * 60 + em) - (sh * 60 + sm));
        }, 0);
        result.hours_played = Math.round(minutes / 60);
      })(),
    );
  }

  if (wanted.has('membership_tier')) {
    promises.push(
      (async () => {
        const { data } = await client
          .from('user_memberships')
          .select('membership_plan:plan_id(name)')
          .eq('user_id', userId)
          .eq('account_id', accountId)
          .eq('status', 'active')
          .eq('is_deleted', false)
          .maybeSingle();
        const tier = (data as { membership_plan?: { name?: string } } | null)
          ?.membership_plan?.name;
        result.membership_tier = tier ?? null;
      })(),
    );
  }

  if (wanted.has('streak_days')) {
    // Placeholder — real streak requires a dedicated function. For now,
    // count distinct booking days in the last 30 days.
    promises.push(
      (async () => {
        const { data } = await (client.rpc as any)('get_family_bookings', {
          p_user_id: userId,
          p_tenant_id: accountId,
          p_include_past: true,
        });
        const all = ((data ?? []) as any[]).flatMap((account: any) => account.bookings ?? []);
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - 30);
        const days = new Set(
          all
            .filter((b: any) => new Date(b.booking_date) >= cutoff)
            .map((b: any) => b.booking_date),
        );
        result.streak_days = days.size > 0 ? `${days.size}d` : '0d';
      })(),
    );
  }

  await Promise.all(promises);
  return result;
}
